import Service from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { service } from '@ember/service';

export default class HealthMonitorService extends Service {
  @service('connection-optimizer') connectionOptimizer;
  @service('graphql-performance') graphqlPerformance;
  @service('api-security') apiSecurity;
  @service('job-queue') jobQueue;

  @tracked healthStatus = 'unknown';
  @tracked lastHealthCheck = null;
  @tracked componentHealth = new Map();

  // Health check intervals
  INTERVALS = {
    critical: 30000, // 30 seconds
    important: 60000, // 1 minute
    normal: 300000, // 5 minutes
  };

  constructor() {
    super(...arguments);
    this.startHealthMonitoring();
  }

  startHealthMonitoring() {
    // Run immediate health check
    this.runFullHealthCheck();

    // Schedule periodic health checks
    setInterval(() => {
      this.runFullHealthCheck();
    }, this.INTERVALS.normal);

    // Schedule critical component checks
    setInterval(() => {
      this.runCriticalChecks();
    }, this.INTERVALS.critical);
  }

  async runFullHealthCheck() {
    const startTime = Date.now();
    const results = {};

    try {
      // Check all components
      results.database = await this.checkDatabaseHealth();
      results.graphql = await this.checkGraphQLHealth();
      results.security = await this.checkSecurityHealth();
      results.jobs = await this.checkJobQueueHealth();
      results.memory = await this.checkMemoryHealth();
      results.performance = await this.checkPerformanceHealth();
      results.external = await this.checkExternalServices();

      // Determine overall health
      const overallHealth = this.calculateOverallHealth(results);

      this.lastHealthCheck = {
        timestamp: new Date().toISOString(),
        duration: Date.now() - startTime,
        status: overallHealth.status,
        score: overallHealth.score,
        components: results,
      };

      this.healthStatus = overallHealth.status;

      // Store individual component health
      Object.entries(results).forEach(([component, health]) => {
        this.componentHealth.set(component, health);
      });

      console.info('Health check completed:', {
        status: overallHealth.status,
        score: overallHealth.score,
        duration: this.lastHealthCheck.duration,
      });
    } catch (error) {
      console.error('Health check failed:', error);
      this.healthStatus = 'error';
    }
  }

  async runCriticalChecks() {
    // Quick checks for critical components only
    const results = {};

    try {
      results.database = await this.checkDatabaseHealth();
      results.memory = await this.checkMemoryHealth();

      // Update component health for critical items
      Object.entries(results).forEach(([component, health]) => {
        this.componentHealth.set(component, health);
      });
    } catch (error) {
      console.warn('Critical health check failed:', error);
    }
  }

  async checkDatabaseHealth() {
    try {
      const connectionStats = this.connectionOptimizer?.getConnectionStats();

      if (!connectionStats) {
        return {
          status: 'unknown',
          message: 'Connection optimizer not available',
        };
      }

      const healthyPools = connectionStats.overall.healthyPools;
      const totalPools = connectionStats.overall.totalPools;
      const healthPercentage =
        totalPools > 0 ? (healthyPools / totalPools) * 100 : 100;

      return {
        status:
          healthPercentage >= 80
            ? 'healthy'
            : healthPercentage >= 50
              ? 'degraded'
              : 'unhealthy',
        healthPercentage,
        totalConnections: connectionStats.overall.totalConnections,
        activeConnections: connectionStats.overall.totalActiveConnections,
        healthyPools,
        totalPools,
        details: connectionStats.pools,
      };
    } catch (error) {
      return {
        status: 'error',
        message: error.message,
        error: true,
      };
    }
  }

  async checkGraphQLHealth() {
    try {
      const metrics = this.graphqlPerformance?.getPerformanceMetrics();

      if (!metrics) {
        return {
          status: 'unknown',
          message: 'GraphQL performance service not available',
        };
      }

      const errorRate = metrics.errorQueries / metrics.totalQueries;
      const slowQueryRate = metrics.slowQueries / metrics.totalQueries;

      let status = 'healthy';
      if (errorRate > 0.05 || slowQueryRate > 0.1) {
        status = 'degraded';
      }
      if (errorRate > 0.15 || slowQueryRate > 0.25) {
        status = 'unhealthy';
      }

      return {
        status,
        errorRate: Math.round(errorRate * 100) / 100,
        slowQueryRate: Math.round(slowQueryRate * 100) / 100,
        totalQueries: metrics.totalQueries,
        averageComplexity: Math.round(metrics.averageComplexity),
        topSlowQueries: metrics.topSlowQueries?.slice(0, 3) || [],
      };
    } catch (error) {
      return {
        status: 'error',
        message: error.message,
        error: true,
      };
    }
  }

  async checkSecurityHealth() {
    try {
      const securityStats = this.apiSecurity?.getSecurityStats();

      if (!securityStats) {
        return { status: 'unknown', message: 'Security service not available' };
      }

      const recentEvents = securityStats.recentEvents;
      const suspiciousActivity = Object.entries(
        securityStats.eventsByType,
      ).filter(
        ([type, count]) =>
          [
            'request_blocked',
            'suspicious_content',
            'rate_limit_exceeded',
          ].includes(type) && count > 10,
      ).length;

      let status = 'healthy';
      if (suspiciousActivity > 0 || recentEvents > 100) {
        status = 'warning';
      }
      if (suspiciousActivity > 3 || recentEvents > 500) {
        status = 'alert';
      }

      return {
        status,
        recentEvents,
        suspiciousActivity,
        blockedIPs: securityStats.blockedIPs,
        activeLimiters: securityStats.activeLimiters,
        eventsByType: securityStats.eventsByType,
      };
    } catch (error) {
      return {
        status: 'error',
        message: error.message,
        error: true,
      };
    }
  }

  async checkJobQueueHealth() {
    try {
      const jobStats = this.jobQueue?.getJobStats();

      if (!jobStats) {
        return {
          status: 'unknown',
          message: 'Job queue service not available',
        };
      }

      const totalJobs =
        jobStats.completed +
        jobStats.failed +
        jobStats.running +
        jobStats.queued;
      const failureRate = totalJobs > 0 ? jobStats.failed / totalJobs : 0;

      let status = 'healthy';
      if (failureRate > 0.1 || jobStats.queued > 50) {
        status = 'degraded';
      }
      if (failureRate > 0.25 || jobStats.queued > 100) {
        status = 'unhealthy';
      }

      return {
        status,
        failureRate: Math.round(failureRate * 100) / 100,
        ...jobStats,
      };
    } catch (error) {
      return {
        status: 'error',
        message: error.message,
        error: true,
      };
    }
  }

  async checkMemoryHealth() {
    try {
      const memoryInfo = this.getMemoryInfo();

      let status = 'healthy';
      if (memoryInfo.heapUsedPercentage > 80) {
        status = 'warning';
      }
      if (memoryInfo.heapUsedPercentage > 95) {
        status = 'critical';
      }

      return {
        status,
        ...memoryInfo,
      };
    } catch (error) {
      return {
        status: 'error',
        message: error.message,
        error: true,
      };
    }
  }

  async checkPerformanceHealth() {
    try {
      const performanceInfo = this.getPerformanceInfo();

      let status = 'healthy';
      if (performanceInfo.averageResponseTime > 2000) {
        status = 'degraded';
      }
      if (performanceInfo.averageResponseTime > 5000) {
        status = 'unhealthy';
      }

      return {
        status,
        ...performanceInfo,
      };
    } catch (error) {
      return {
        status: 'error',
        message: error.message,
        error: true,
      };
    }
  }

  async checkExternalServices() {
    const services = [
      { name: 'contentful', url: 'https://cdn.contentful.com' },
      { name: 'analytics', url: 'https://www.google-analytics.com' },
    ];

    const results = {};

    for (const service of services) {
      try {
        const startTime = Date.now();
        const response = await fetch(service.url, {
          method: 'HEAD',
          timeout: 5000,
        });

        const responseTime = Date.now() - startTime;

        results[service.name] = {
          status: response.ok ? 'healthy' : 'unhealthy',
          responseTime,
          httpStatus: response.status,
        };
      } catch (error) {
        results[service.name] = {
          status: 'error',
          message: error.message,
          error: true,
        };
      }
    }

    // Overall external services health
    const healthyServices = Object.values(results).filter(
      (r) => r.status === 'healthy',
    ).length;
    const totalServices = Object.keys(results).length;
    const healthPercentage = (healthyServices / totalServices) * 100;

    return {
      status:
        healthPercentage >= 80
          ? 'healthy'
          : healthPercentage >= 50
            ? 'degraded'
            : 'unhealthy',
      healthPercentage,
      services: results,
    };
  }

  getMemoryInfo() {
    if (typeof performance !== 'undefined' && performance.memory) {
      const memory = performance.memory;
      return {
        heapUsed: Math.round((memory.usedJSHeapSize / 1024 / 1024) * 100) / 100, // MB
        heapTotal:
          Math.round((memory.totalJSHeapSize / 1024 / 1024) * 100) / 100, // MB
        heapLimit:
          Math.round((memory.jsHeapSizeLimit / 1024 / 1024) * 100) / 100, // MB
        heapUsedPercentage: Math.round(
          (memory.usedJSHeapSize / memory.jsHeapSizeLimit) * 100,
        ),
      };
    }

    return {
      heapUsed: 'unknown',
      heapTotal: 'unknown',
      heapLimit: 'unknown',
      heapUsedPercentage: 0,
    };
  }

  getPerformanceInfo() {
    if (typeof performance !== 'undefined' && performance.timing) {
      const timing = performance.timing;
      const navigation = performance.getEntriesByType('navigation')[0];

      return {
        pageLoadTime: timing.loadEventEnd - timing.navigationStart,
        domContentLoaded:
          timing.domContentLoadedEventEnd - timing.navigationStart,
        averageResponseTime:
          navigation?.responseEnd - navigation?.responseStart || 0,
        redirectTime: timing.redirectEnd - timing.redirectStart,
        dnsLookupTime: timing.domainLookupEnd - timing.domainLookupStart,
      };
    }

    return {
      pageLoadTime: 'unknown',
      domContentLoaded: 'unknown',
      averageResponseTime: 'unknown',
      redirectTime: 'unknown',
      dnsLookupTime: 'unknown',
    };
  }

  calculateOverallHealth(results) {
    const weights = {
      database: 30,
      graphql: 25,
      security: 15,
      jobs: 10,
      memory: 10,
      performance: 5,
      external: 5,
    };

    let totalScore = 0;
    let maxScore = 0;

    Object.entries(results).forEach(([component, health]) => {
      const weight = weights[component] || 1;
      maxScore += weight;

      let score = 0;
      switch (health.status) {
        case 'healthy':
          score = weight;
          break;
        case 'degraded':
        case 'warning':
          score = weight * 0.7;
          break;
        case 'unhealthy':
        case 'alert':
          score = weight * 0.3;
          break;
        case 'critical':
        case 'error':
          score = 0;
          break;
        default:
          score = weight * 0.5; // unknown
      }

      totalScore += score;
    });

    const healthScore = Math.round((totalScore / maxScore) * 100);

    let overallStatus = 'healthy';
    if (healthScore < 90) overallStatus = 'degraded';
    if (healthScore < 70) overallStatus = 'unhealthy';
    if (healthScore < 40) overallStatus = 'critical';

    return {
      status: overallStatus,
      score: healthScore,
    };
  }

  getHealthSummary() {
    return {
      status: this.healthStatus,
      lastCheck: this.lastHealthCheck,
      components: Object.fromEntries(this.componentHealth),
    };
  }

  isHealthy() {
    return ['healthy', 'degraded'].includes(this.healthStatus);
  }

  getHealthEndpoint() {
    // Returns health data in a format suitable for load balancers
    const isHealthy = this.isHealthy();

    return {
      status: isHealthy ? 'UP' : 'DOWN',
      timestamp: new Date().toISOString(),
      version: '1.0.0',
      checks: Object.fromEntries(
        Array.from(this.componentHealth.entries()).map(([name, health]) => [
          name,
          { status: health.status === 'healthy' ? 'UP' : 'DOWN' },
        ]),
      ),
    };
  }
}
